import React from 'react';
import cn from 'classnames';
import { ISpuInfo, IReview, IVariant } from 'base/interfaces/product';
import { useRouter } from 'next/router';
import { Rate } from '@base/lib/Rate';
import ProductPrice from '@base/snippets/ProductPriceV2';
import { IAddCartInstance } from '@base/product/useAddCart';
import styles from './index.module.less';
import ProductCoupons from '@app/product/ProductCoupons';
import ProdFlashSaleBar from '@base/product/ProdFlashSaleBar';
import ProductPoints from '@app/product/ProductPoints';
import ProdBundleDeals from '@app/product/ProdBundleDeals';
import AfterPayRemindModal from '@base/common/payMent/AfterPay/AfterPayRemindModal';
import AffirmPayRemindModal from '@base/common/payMent/AffirmPay/AffirmPayRemindModal';
import KlarnaRemindModal from '@base/common/payMent/Klarna/KlarnaRemindModal';
import Image from '@base/lib/ThemeImage';
import ProductOffTag from '@app/product/ProductOffTag';
import { ProdTitle } from '@base/product/ProdTitle';
import { ProdGifts } from '@app/common/ProdGifts';
import { DeliveryTip } from '@base/product/DeliveryTip';
import { ProdVat } from 'mod/product/ProdVat';
import { ProdSku } from 'mod/product/ProdSku';
import { replacePlaceholderWithComponent } from 'base/utils/string';
import { EnergyLabel } from '@pro/common/EnergyLabel';

interface IProps {
    addCart: IAddCartInstance;
    isQuickView?: boolean;
    onCloseQuickView?: () => void; //关闭quick view
    shownVariant: IVariant;
    spuInfo: ISpuInfo;
    reviewInfo: IReview;
    onClickReview?: () => void;
    canJump?: boolean; // 是否可以跳转到商品详情页
}

const ProdInfo: React.FC<IProps> = props => {
    const router = useRouter();

    const { isQuickView, onCloseQuickView, shownVariant, spuInfo, reviewInfo, onClickReview, canJump = true } = props

    const { addCart } = props;
    const { availableCoupons } = addCart;

    const variantUrl = shownVariant?.url || `${spuInfo?.url}${shownVariant?.productVariantId ? `?variantId=${shownVariant?.productVariantId}` : ''}`;

    return (
        <>
            <DeliveryTip shownVariant={shownVariant}/>

            <ProdTitle shownVariant={shownVariant}/>

            {/* energyLabel */}
            {!!shownVariant.energyLabel && (
                <EnergyLabel
                    level={shownVariant.energyLabel.level}
                    pdf={shownVariant.energyLabel.pdf}
                    pic={shownVariant.energyLabel.pic}
                    style={{ margin: '0 0 16px' }}
                />
            )}

            <div className={styles['prices-rate']}>
                <div className={'flex flex-1'}>
                    {/* 价格 */}
                    <ProductPrice
                        className={cn(styles.prices)}
                        product={shownVariant as IVariant}
                        direction="horizontal"
                    />

                    {shownVariant.vat && <ProdVat className={styles.vat}/>}

                    {/* save标签 */}
                    <ProductOffTag shownVariant={shownVariant}/>
                </div>

                {/* 评分 */}
                {reviewInfo && Number(reviewInfo.score) > 0 && (
                    <Rate
                        starSize={22}
                        textSize={18}
                        value={reviewInfo.score ? +reviewInfo.score : 5}
                        total={reviewInfo.reviewNum}
                        onClick={!canJump
                            ? void 0
                            : isQuickView
                                ? () => {
                                    onCloseQuickView?.();
                                    router.push(`${variantUrl}#review-list`)
                                }
                                : onClickReview}
                        type="simple"
                    />
                )}
            </div>

            {/* adyen Affirm pay tips */}
            {!!shownVariant.affirmPayTips && (
                <AffirmPayRemindModal className={styles['after-pay-wrap']}>
                    <div className={cn(styles['after-pay-tips'], 'flex flex-middle')}>
                        {replacePlaceholderWithComponent(shownVariant.affirmPayTips, {
                            paymentMethod: (
                                <Image
                                    className={styles['after-pay-img']}
                                    src={'asset/width-264/picture/3886cb9b28d65ff863c8f97e2369e917.png'}
                                    width={50}
                                    height={20}
                                    fixed
                                    background={false}
                                />
                            )
                        })}
                    </div>
                </AffirmPayRemindModal>
            )}

            {/* adyen after pay tips */}
            {!!shownVariant.afterPayTips && (
                <AfterPayRemindModal className={styles['after-pay-wrap']}>
                    <div className={cn(styles['after-pay-tips'], 'flex flex-middle')}>
                        {shownVariant.afterPayTips}
                        <Image
                            className={styles['after-pay-img']}
                            src={'asset/width-228/picture/7ef7546c0813d03ec41263364b7d01fe.png'}
                            width={57}
                            height={20}
                            fixed
                            background={false}
                        />
                    </div>
                </AfterPayRemindModal>
            )}

            {/* adyen klarna tips */}
            {!!shownVariant.klarnaPayTips && (
                <KlarnaRemindModal className={styles['after-pay-wrap']}>
                    <div className={cn(styles['after-pay-tips'], 'flex flex-middle')}>
                        {shownVariant.klarnaPayTips}
                        <Image
                            className={styles['after-pay-img']}
                            src={'asset/width-229/picture/de1718dff1bd8233367d8c45e5012603.png'}
                            width={57}
                            height={24}
                            fixed
                            background={false}
                        />
                    </div>
                </KlarnaRemindModal>
            )}

            <ProdSku shownVariant={shownVariant} className={styles['prod-sku']}/>

            <ProdFlashSaleBar shownVariant={shownVariant} className={styles['prod-flash-sale-bar']}/>

            <ProdBundleDeals shownVariant={shownVariant}/>

            <ProdGifts gifts={shownVariant.giftItemList} className={styles.gifts}/>

            <ProductPoints points={shownVariant?.points}/>

            <ProductCoupons
                className={styles['coupons']}
                coupons={availableCoupons}
            />
        </>
    )
}

export default ProdInfo;
